import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { StammkundeService } from '../../../Services/stammkunde.service';
import {
  AUFTRAG_STATUS_LABELS,
  AuftragOverview,
  CustomerRef,
  PersonHours
} from '../../../Models/stammkunde.model';

/**
 * Gesamtübersicht für den Bauleiter über alle seine Projekte: Kennzahlen auf einen
 * Blick, offener Freigabe-Bedarf für Stunden und Material (mit 1-Klick-Freigabe),
 * sowie eine Auswertung "wer hat wie viel gearbeitet".
 */
@Component({
  selector: 'app-bauleiter-uebersicht',
  templateUrl: './bauleiter-uebersicht.component.html',
  styleUrls: ['./bauleiter-uebersicht.component.css', '../stammkunden-theme.css']
})
export class BauleiterUebersichtComponent implements OnInit {
  /** Eingebettet (z.B. als Tab der Stammkunden-Seite): eigener Hero/Seitengerüst entfällt. */
  @Input() embedded = false;
  /** Fest auf einen Stammkunden begrenzt (im Kunden-Tab): kein Kunden-Filter. */
  @Input() fixedCustomerId?: number | null;
  loading = true;
  projects: AuftragOverview[] = [];
  persons: PersonHours[] = [];
  customers: CustomerRef[] = [];
  selectedCustomerId: number | null = null;
  view: 'projekte' | 'personen' = 'projekte';
  busyAuftragId: number | null = null;

  readonly statusLabels = AUFTRAG_STATUS_LABELS;

  constructor(
    private stammkundeService: StammkundeService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.fixedCustomerId != null) {
      // Auf einen Kunden begrenzt – kein Kundenwechsel, kein Verzeichnis nötig.
      this.selectedCustomerId = this.fixedCustomerId;
    } else {
      this.stammkundeService.getMyCustomers().subscribe({
        next: list => this.customers = list ?? [],
        error: () => { /* Filter bleibt leer */ }
      });
    }
    this.load();
  }

  get fixedCustomer(): boolean {
    return this.fixedCustomerId != null;
  }

  load(): void {
    this.loading = true;
    const cid = this.selectedCustomerId ?? undefined;
    forkJoin({
      projects: this.stammkundeService.getOverview(cid),
      persons: this.stammkundeService.getHoursByPerson(cid)
    }).subscribe({
      next: res => {
        this.projects = res.projects ?? [];
        this.persons = res.persons ?? [];
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Übersicht konnte nicht geladen werden.', 'OK', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  selectCustomer(id: number | null): void {
    this.selectedCustomerId = id;
    this.load();
  }

  // ── Kennzahlen ──

  get projectCount(): number { return this.projects.length; }
  get totalHours(): number { return this.round(this.projects.reduce((s, p) => s + p.totalHours, 0)); }
  get hoursToApproveCount(): number { return this.projects.reduce((s, p) => s + p.submittedHourCount, 0); }
  get hoursToApprove(): number { return this.round(this.projects.reduce((s, p) => s + p.submittedHours, 0)); }
  get materialToApprove(): number { return this.projects.reduce((s, p) => s + p.materialSubmittedCount, 0); }
  get projectsNeedingAction(): number {
    return this.projects.filter(p => p.submittedHourCount > 0 || p.materialSubmittedCount > 0).length;
  }

  needsAction(p: AuftragOverview): boolean {
    return p.submittedHourCount > 0 || p.materialSubmittedCount > 0;
  }

  // ── Freigaben (1-Klick) ──

  approveHours(p: AuftragOverview, event: Event): void {
    event.stopPropagation();
    if (p.submittedHourCount === 0 || this.busyAuftragId) { return; }
    this.busyAuftragId = p.auftragId;
    this.stammkundeService.approveAllTimeEntries(p.auftragId).subscribe({
      next: res => {
        this.busyAuftragId = null;
        this.snackBar.open(`${res.approved} Stunden-Eintrag/-Einträge freigegeben.`, 'OK', { duration: 3000 });
        this.load();
      },
      error: err => {
        this.busyAuftragId = null;
        this.snackBar.open(err?.error?.message || 'Freigabe fehlgeschlagen.', 'OK', { duration: 4000 });
      }
    });
  }

  approveMaterials(p: AuftragOverview, event: Event): void {
    event.stopPropagation();
    if (p.materialSubmittedCount === 0 || this.busyAuftragId) { return; }
    this.busyAuftragId = p.auftragId;
    this.stammkundeService.approveAllMaterials(p.auftragId).subscribe({
      next: res => {
        this.busyAuftragId = null;
        this.snackBar.open(`${res.approved} Materialposten freigegeben.`, 'OK', { duration: 3000 });
        this.load();
      },
      error: err => {
        this.busyAuftragId = null;
        this.snackBar.open(err?.error?.message || 'Freigabe fehlgeschlagen.', 'OK', { duration: 4000 });
      }
    });
  }

  openDetail(p: AuftragOverview): void {
    this.router.navigate(['/stammkunden', p.customerId, 'auftrag', p.auftragId]);
  }

  // ── Helpers ──

  personLabel(p: PersonHours): string {
    return p.personName;
  }

  /** Anteil (0..1) der Stunden dieser Person an der meist-gearbeiteten Person (für den Balken). */
  personBar(p: PersonHours): number {
    const max = this.persons.length ? this.persons[0].totalHours : 0;
    return max > 0 ? Math.round((p.totalHours / max) * 100) : 0;
  }

  get personsTotalHours(): number {
    return this.round(this.persons.reduce((s, p) => s + p.totalHours, 0));
  }

  trackByAuftrag(_: number, p: AuftragOverview): number { return p.auftragId; }
  trackByPerson(_: number, p: PersonHours): string {
    return p.workerId != null ? `w${p.workerId}` : (p.personUserId ? `u${p.personUserId}` : `n${p.personName}`);
  }

  private round(v: number): number {
    return Math.round(v * 100) / 100;
  }
}
